/**
 * In progress — every impact assessment that is running, or that ran and produced nothing.
 *
 * Built on 1 October 2026 after Joanne asked for the Mills Review and the Payments review in the
 * same five minutes and could see neither: one was refused for length while she was on another
 * tab, the other finished eleven minutes later into a list that never re-read itself. What she
 * asked for, and what this is:
 *
 * - A run she starts appears here at once and **reports its stage every minute** — step, label,
 *   how long, what it has spent — until it finishes. Finished documents go where every other
 *   document goes, in Generated documents below; this list is only what still needs somebody.
 * - A run that **failed, was refused or stalled stays here with its reason** until she clears it
 *   or a re-run succeeds. (She and Craig are emailed too — see `notify.py` — but the screen is
 *   the record.)
 * - A run that was **too much to read in one go** — refused for length, too many obligations, or
 *   stopped by its own spend or time limit — offers two narrower re-runs: sections she ticks, or
 *   passages the AI picks. Both are the publication's own words, and quotes are still checked
 *   against the whole of it. Each goes through the same price confirmation as "Assess now".
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { api } from "../api";
import type { Assessment, AssessmentMode, AssessmentPreview } from "../types";
import { Confirm, money } from "./AssessNow";

/** How often a list with something running asks again. "An update every few minutes" — and a
 *  stage lasts two to four, so once a minute shows each one without hammering anything. */
const POLL_MS = 60_000;

/** While the worker lists a stopped run's sections — a fetch, seconds not minutes. */
const QUICK_POLL_MS = 10_000;

const FAILED_IN_HER_WORDS: Record<string, string> = {
  over_budget: "It reached its spending limit before it could finish.",
  over_time: "It ran out of time before it could finish.",
  stalled:
    "It stopped without finishing and without being able to say why — most likely it ran out " +
    "of time on a long publication.",
  other: "Something went wrong on our side. Craig has been told.",
};

function running(job: Assessment): boolean {
  return job.status === "queued" || job.status === "generating";
}

function minutesSince(iso?: string): number | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.round((Date.now() - then) / 60_000));
}

function ago(iso?: string): string {
  const minutes = minutesSince(iso);
  if (minutes === null) return "";
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${minutes % 60} min ago`;
}

function ukTime(iso: string): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "";
  return when.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function thousands(characters: number): string {
  return `${Math.round(characters / 1000).toLocaleString("en-GB")}k`;
}

/** What she is about to confirm: which run, and how it will read the publication. */
interface Pending {
  job: Assessment;
  mode: AssessmentMode;
  sections: number[];
  reading?: string;
  preview: AssessmentPreview | null;
}

export function RunsInProgress({
  onError,
  refresh = 0,
  onFinished,
}: {
  onError: (error: unknown) => void;
  /** Bumped by the screen when a run is started elsewhere, so it shows here without a wait. */
  refresh?: number;
  /** A run here has finished with a document — the Generated documents list should re-read. */
  onFinished?: () => void;
}) {
  const [jobs, setJobs] = useState<Assessment[] | null>(null);
  const [readLimit, setReadLimit] = useState(200_000);
  const [picking, setPicking] = useState<string | null>(null);
  const [ticked, setTicked] = useState<Record<string, number[]>>({});
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** The running ids last time, to notice one that has left the list because it finished. */
  const runningBefore = useRef<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const page = await api.activeAssessments();
      const now = new Set(page.items.filter(running).map((job) => job.item_id));
      const listed = new Set(page.items.map((job) => job.item_id));
      const finished = [...runningBefore.current].some((id) => !listed.has(id));
      runningBefore.current = now;
      setJobs(page.items);
      setReadLimit((page as { read_limit?: number }).read_limit ?? 200_000);
      if (finished) onFinished?.();
    } catch (error) {
      onError(error);
    }
  }, [onError, onFinished]);

  useEffect(() => {
    void load();
  }, [load, refresh]);

  // Poll while something is moving: a run, or a section list being made. Nothing moving, no timer.
  useEffect(() => {
    if (!jobs) return;
    const listing = jobs.some((job) => job.outline_requested_at);
    if (!listing && !jobs.some(running)) return;
    const timer = window.setTimeout(() => void load(), listing ? QUICK_POLL_MS : POLL_MS);
    return () => window.clearTimeout(timer);
  }, [jobs, load]);

  const dismiss = async (job: Assessment) => {
    setBusy(job.item_id);
    try {
      await api.dismissAssessment(job.item_id);
      setJobs((current) => (current ?? []).filter((row) => row.item_id !== job.item_id));
    } catch (error) {
      onError(error);
    } finally {
      setBusy(null);
    }
  };

  const listSections = async (job: Assessment) => {
    setBusy(job.item_id);
    try {
      await api.outlineAssessment(job.item_id);
      await load();
    } catch (error) {
      onError(error);
    } finally {
      setBusy(null);
    }
  };

  /** Show the price first, exactly as "Assess now" does. Nothing is spent until Generate. */
  const ask = async (job: Assessment, mode: AssessmentMode, sections: number[] = []) => {
    let reading: string | undefined;
    if (mode === "passages") {
      reading = "passages the AI picks from it — about 80k characters of its own words";
    } else if (mode === "sections") {
      const chosen = (job.outline ?? []).filter((section) => sections.includes(section.index));
      const total = chosen.reduce((sum, section) => sum + section.characters, 0);
      reading = `the ${chosen.length} section${chosen.length === 1 ? "" : "s"} you chose (${thousands(total)} characters)`;
    }
    setPending({ job, mode, sections, reading, preview: null });
    try {
      const preview = await api.previewAssessment(job.item_id);
      setPending({ job, mode, sections, reading, preview });
    } catch (error) {
      setPending(null);
      onError(error);
    }
  };

  const start = async () => {
    if (!pending) return;
    setBusy(pending.job.item_id);
    try {
      await api.createAssessment(pending.job.item_id, {
        mode: pending.mode,
        sections: pending.mode === "sections" ? pending.sections : undefined,
      });
      setPending(null);
      setPicking(null);
      await load();
    } catch (error) {
      onError(error);
    } finally {
      setBusy(null);
    }
  };

  if (jobs === null) return null;

  return (
    <div className="card runs-in-progress">
      <h3>In progress</h3>
      {jobs.length === 0 ? (
        <p className="muted small">
          Nothing running, and nothing waiting for a decision. A run you start appears here with
          its progress; when it finishes, the document is in the list below.
        </p>
      ) : (
        <p className="muted small">
          Runs still going update every minute. A run that produced nothing stays here, with the
          reason, until you clear it or run it again. You are emailed about those too.
        </p>
      )}

      {jobs.map((job) => {
        const choosing = picking === job.item_id;
        const chosen = ticked[job.item_id] ?? [];
        const chosenTotal = (job.outline ?? [])
          .filter((section) => chosen.includes(section.index))
          .reduce((sum, section) => sum + section.characters, 0);
        const confirming = pending?.job.item_id === job.item_id;

        return (
          <div key={job.item_id} className="preview run-row">
            <p className="small">
              <strong>{job.item_title}</strong>
              <span className="muted">
                {" "}
                · asked for {ukTime(job.requested_at)}
                {job.requested_by.includes("@") ? ` by ${job.requested_by.split("@")[0]}` : ""}
                {job.mode && job.mode !== "whole"
                  ? job.mode === "sections"
                    ? " · chosen sections"
                    : " · AI-picked passages"
                  : ""}
              </span>
            </p>

            {running(job) && <Running job={job} />}
            {!running(job) && <Stopped job={job} />}

            {!running(job) && !confirming && (
              <div className="row run-actions">
                {job.can_narrow && (
                  <>
                    <button
                      type="button"
                      disabled={!!busy}
                      onClick={() => void ask(job, "passages")}
                      title="An AI reads the whole publication and picks the passages that matter, in their own words. It costs a little more than reading those passages alone."
                    >
                      Let the AI pick passages
                    </button>
                    {job.outline && job.outline.length > 0 ? (
                      <button
                        type="button"
                        disabled={!!busy}
                        onClick={() => setPicking(choosing ? null : job.item_id)}
                      >
                        {choosing ? "Hide sections" : "Pick sections"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={!!busy || !!job.outline_requested_at}
                        onClick={() => void listSections(job)}
                        title="Fetches the publication and lists its sections. No model is used and nothing is spent."
                      >
                        {job.outline_requested_at ? "Listing sections…" : "List its sections (free)"}
                      </button>
                    )}
                  </>
                )}
                {!job.can_narrow && (
                  <button type="button" disabled={!!busy} onClick={() => void ask(job, "whole")}>
                    Run it again
                  </button>
                )}
                <button
                  type="button"
                  className="link"
                  disabled={busy === job.item_id}
                  onClick={() => void dismiss(job)}
                >
                  Clear
                </button>
              </div>
            )}

            {choosing && !confirming && job.outline && (
              <div className="section-picker">
                <p className="small muted">
                  Tick what the assessment should read. One run reads up to{" "}
                  {thousands(readLimit)} characters; the rest is not read, and the document says
                  which sections it covered.
                </p>
                <ul>
                  {job.outline.map((section) => (
                    <li key={section.index}>
                      <label>
                        <input
                          type="checkbox"
                          checked={chosen.includes(section.index)}
                          onChange={(event) =>
                            setTicked((current) => ({
                              ...current,
                              [job.item_id]: event.target.checked
                                ? [...chosen, section.index]
                                : chosen.filter((index) => index !== section.index),
                            }))
                          }
                        />{" "}
                        {section.title}{" "}
                        <span className="muted">({thousands(section.characters)})</span>
                      </label>
                    </li>
                  ))}
                </ul>
                <p className={`small ${chosenTotal > readLimit ? "ai-warning" : "muted"}`}>
                  Chosen: {thousands(chosenTotal)} of {thousands(readLimit)} characters
                  {chosenTotal > readLimit ? " — too much for one run; untick something." : "."}
                </p>
                <button
                  type="button"
                  className="primary"
                  disabled={!!busy || chosen.length === 0 || chosenTotal > readLimit}
                  onClick={() => void ask(job, "sections", chosen)}
                >
                  Assess these sections
                </button>
              </div>
            )}

            {confirming && !pending?.preview && <p className="small muted">Working out the price…</p>}
            {confirming && pending?.preview && (
              <Confirm
                preview={pending.preview}
                busy={busy === job.item_id}
                onCancel={() => setPending(null)}
                onProceed={() => void start()}
                reading={pending.reading}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Running({ job }: { job: Assessment }) {
  if (job.stalled) {
    return (
      <p className="small ai-warning">
        <strong>Stalled</strong> — nothing has happened for over twenty minutes. Within ten
        minutes it will be marked as failed, and you will be emailed.
      </p>
    );
  }
  if (job.status === "queued") {
    return (
      <p className="small">
        <strong>Queued</strong>
        {job.ahead
          ? ` — waiting for ${job.ahead} run${job.ahead === 1 ? "" : "s"} ahead of it to finish. Runs go one at a time.`
          : " — starting shortly."}
      </p>
    );
  }
  const spent = job.cost_pence ?? 0;
  return (
    <p className="small">
      {job.stage_number && job.stage_count ? (
        <strong>
          Step {job.stage_number} of {job.stage_count}: {job.stage_label || job.stage}
        </strong>
      ) : (
        <strong>Starting</strong>
      )}
      <span className="muted">
        {" "}
        · started {ago(job.started_at || job.requested_at)}
        {job.stage_started_at ? ` · this step began ${ago(job.stage_started_at)}` : ""}
        {spent > 0 ? ` · ${money(spent)} spent so far` : ""}
      </span>
    </p>
  );
}

function Stopped({ job }: { job: Assessment }) {
  const spent = job.cost_pence ?? 0;
  const label =
    job.problem === "stalled" ? "Stalled" : job.status === "refused" ? "Not run" : "Failed";
  const words =
    job.status === "refused" ? job.message : FAILED_IN_HER_WORDS[job.problem || "other"];

  return (
    <>
      <p className={`small ${job.status === "refused" ? "" : "ai-warning"}`}>
        <strong>{label}.</strong> {words}
      </p>
      <p className="small muted">
        {spent > 0 ? `This run cost ${money(spent)} and produced no document.` : "Nothing was spent."}
        {job.can_narrow &&
          " It is too much to read in one go, so it can be run again on part of it."}
      </p>
      {job.status === "failed" && job.message && job.problem !== "stalled" && (
        <details className="small muted">
          <summary>Technical detail</summary>
          <code>{job.message}</code>
        </details>
      )}
    </>
  );
}
